/**
 * Owner: Masa Bayu
 * File: qx-writer-page-section-paint.ts
 * Date:Time: 2026-10-16 07:52
 * Description: Section page span · range assign · paint values.
 */

import {
  defaultPageSection,
  ensurePageSections,
  formatSectionPageValue,
  listSectionSpineSheets,
  readPageSections,
  SECTION_BREAK_ATTR,
  SECTION_ID_ATTR,
  sectionSheetMatter,
  sheetSectionId,
  writePageSections,
  type PageSectionProps,
  type SectionPagePaint,
} from './qx-writer-page-sections';

export type SectionPageSpan = {
  readonly startPage: number;
  readonly endPage: number;
  readonly pageCount: number;
};

/** 1-based document page span covered by a section (inclusive). */
export function sectionPageSpan(root: HTMLElement, sectionId: number): SectionPageSpan {
  const sheets = listSectionSpineSheets(root);
  const pageCount = Math.max(1, sheets.length);
  const indices: number[] = [];
  sheets.forEach((sheet, index) => {
    if (sheetSectionId(sheet) === sectionId) indices.push(index);
  });
  if (indices.length === 0) {
    return { startPage: 1, endPage: pageCount, pageCount };
  }
  return {
    startPage: indices[0]! + 1,
    endPage: indices[indices.length - 1]! + 1,
    pageCount,
  };
}

function restampSectionBreaks(sheets: readonly HTMLElement[]): void {
  let prevId = -1;
  for (const sheet of sheets) {
    const id = sheetSectionId(sheet);
    if (id !== prevId) {
      if (prevId >= 0) sheet.setAttribute(SECTION_BREAK_ATTR, 'next-page');
      else sheet.removeAttribute(SECTION_BREAK_ATTR);
      prevId = id;
    } else {
      sheet.removeAttribute(SECTION_BREAK_ATTR);
    }
  }
  if (sheets[0]) sheets[0].removeAttribute(SECTION_BREAK_ATTR);
}

function takeUnnumberedSection(
  sections: readonly PageSectionProps[],
  avoidId: number,
): { readonly sections: PageSectionProps[]; readonly id: number } {
  const reuse = sections.find(
    (section) => section.id !== avoidId && section.format === 'none',
  );
  if (reuse) return { sections: [...sections], id: reuse.id };
  const id = sections.length;
  return {
    id,
    sections: [...sections, defaultPageSection(id, 'none')],
  };
}

/**
 * Assign physical pages [startPage, endPage] (1-based, inclusive) to sectionId.
 * Overflow pages become unnumbered until the author chooses a format.
 */
export function applySectionPageRange(
  root: HTMLElement,
  sectionId: number,
  startPage: number,
  endPage: number,
): SectionPageSpan {
  const sheets = listSectionSpineSheets(root);
  const n = sheets.length;
  if (n === 0) return { startPage: 1, endPage: 1, pageCount: 1 };
  ensurePageSections(root);
  let start = Math.max(1, Math.min(n, Math.floor(startPage)));
  let end = Math.max(1, Math.min(n, Math.floor(endPage)));
  if (start > end) {
    const swap = start;
    start = end;
    end = swap;
  }
  const startIdx = start - 1;
  const endIdx = end - 1;

  let sections = readPageSections(root);
  const needsBefore = sheets.some(
    (sheet, index) => index < startIdx && sheetSectionId(sheet) === sectionId,
  );
  const needsAfter = sheets.some(
    (sheet, index) => index > endIdx && sheetSectionId(sheet) === sectionId,
  );

  let beforeId: number | null = null;
  let afterId: number | null = null;
  if (needsBefore) {
    const taken = takeUnnumberedSection(sections, sectionId);
    sections = taken.sections;
    beforeId = taken.id;
  }
  if (needsAfter) {
    const taken = takeUnnumberedSection(sections, sectionId);
    sections = taken.sections;
    afterId = taken.id;
    if (beforeId !== null && afterId === beforeId) {
      const id = sections.length;
      sections = [...sections, defaultPageSection(id, 'none')];
      afterId = id;
    }
  }
  writePageSections(root, sections);

  for (let i = 0; i < n; i += 1) {
    const sheet = sheets[i]!;
    const was = sheetSectionId(sheet);
    if (i >= startIdx && i <= endIdx) {
      sheet.setAttribute(SECTION_ID_ATTR, String(sectionId));
      continue;
    }
    if (was !== sectionId) continue;
    if (i < startIdx && beforeId !== null) {
      sheet.setAttribute(SECTION_ID_ATTR, String(beforeId));
    } else if (i > endIdx && afterId !== null) {
      sheet.setAttribute(SECTION_ID_ATTR, String(afterId));
    }
  }

  const maxId = sheets.reduce((max, sheet) => Math.max(max, sheetSectionId(sheet)), 0);
  sections = readPageSections(root);
  while (sections.length <= maxId) {
    sections = [...sections, defaultPageSection(sections.length, 'none')];
  }
  writePageSections(root, sections);
  restampSectionBreaks(sheets);
  return sectionPageSpan(root, sectionId);
}

/** Paint values for each spine sheet under Word section rules. */
export function computeSectionPagePaints(root: HTMLElement): SectionPagePaint[] {
  const sheets = listSectionSpineSheets(root);
  const sections = ensurePageSections(root);
  const paints: SectionPagePaint[] = [];
  let prevValue = 0;
  const indexInSection = new Map<number, number>();

  for (const sheet of sheets) {
    const sectionId = sheetSectionId(sheet);
    const props = sections.find((section) => section.id === sectionId)
      ?? defaultPageSection(sectionId);
    const matter = sectionSheetMatter(sheet);

    if (matter === 'cover') {
      paints.push({
        text: '',
        visible: false,
        value: prevValue,
        sectionId,
        align: props.align,
        italic: props.italic,
      });
      continue;
    }

    const seen = indexInSection.get(sectionId) ?? 0;
    indexInSection.set(sectionId, seen + 1);

    let value: number;
    if (props.linkToPrevious && sectionId > 0 && prevValue > 0) {
      value = prevValue + 1;
    } else {
      value = props.startAt + seen;
    }
    const formatted = formatSectionPageValue(props.format, value, props.label);
    paints.push({
      text: formatted.text,
      visible: formatted.visible,
      value,
      sectionId,
      align: props.align,
      italic: props.italic,
    });
    if (props.format !== 'none') prevValue = value;
  }
  return paints;
}
